import {
  Badge,
  Box,
  Divider,
  Grid,
  Heading,
  HStack,
  Image,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ReferralSuccessModal } from './ReferralSuccessModal';
import { useTheme } from '@/contexts/ThemeProvider';
import { useState } from 'react';
import { useReferrals, useReferralSummary, useCreateReferral, useCheckReferral } from '@/service/referral/hooks';
import { Input } from '@/components/atoms/Input';
import { Button } from '@/components/atoms/Button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { cleanCPFCNPJ } from '@/service/utils';
import { IReferral, IReferralSummary, ReferralStatus } from '@/service/referral/types';
import { useQueryClient } from 'react-query';

const referralFormSchema = z.object({
  document: z.string().min(11, { message: 'CPF/CNPJ é obrigatório' }),
  name: z.string().min(1, { message: 'Nome é obrigatório' }),
  telephone: z.string().min(1, { message: 'Telefone é obrigatório' }),
  email: z.string().email({ message: 'Email inválido' }),
});

type ReferralFormData = z.infer<typeof referralFormSchema>;

const STATUS_LABELS: Record<ReferralStatus, string> = {
  created: 'Aguardando',
  confirmed: 'Confirmada',
  paid: 'Paga',
  expired: 'Expirada',
};

const STATUS_COLORS: Record<ReferralStatus, string> = {
  created: 'yellow',
  confirmed: 'green',
  paid: 'blue',
  expired: 'red',
};

function formatDocument(doc: string): string {
  const clean = doc.replace(/\D/g, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (clean.length === 14) {
    return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return doc;
}

function ReferralListItem({ referral }: { referral: IReferral }) {
  return (
    <Box width="100%" padding="16px" borderRadius="lg" boxShadow="2px 2px 6px 0px #0000002E" backgroundColor="white">
      <HStack justifyContent="space-between" alignItems="flex-start">
        <VStack align="flex-start" spacing={1} flex={1} minWidth={0}>
          <Text fontWeight={600} fontSize="15px" color="#292927" noOfLines={1}>
            {referral.name || formatDocument(referral.document)}
          </Text>
          <Text fontSize="13px" color="#747474">
            {formatDocument(referral.document)}
          </Text>
          {referral.telephone && (
            <Text fontSize="12px" color="#747474">
              {referral.telephone}
            </Text>
          )}
          {referral.email && (
            <Text fontSize="12px" color="#747474" noOfLines={1}>
              {referral.email}
            </Text>
          )}
          <Text fontSize="12px" color="#b0b0b0">
            {new Date(referral.createdAt).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </Text>
        </VStack>
        <Badge colorScheme={STATUS_COLORS[referral.status]} borderRadius="full" px={3} py={1} flexShrink={0}>
          {STATUS_LABELS[referral.status]}
        </Badge>
      </HStack>
      {referral.status === 'paid' && referral.valuePaid && (
        <Text mt={2} fontSize="13px" color="green.600" fontWeight={600}>
          Recebido: R$ {Number(referral.valuePaid).toFixed(2).replace('.', ',')}
        </Text>
      )}
    </Box>
  );
}

export function Referral() {
  const { colors, id: agreementId, referralImage, referralOffer, referralValue } = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [submittedData, setSubmittedData] = useState<ReferralFormData | null>(null);

  const { data: referralsData, isLoading: loadingReferrals } = useReferrals();
  const { data: summary } = useReferralSummary();

  const totalByStatus = (summary?.totalByStatus ?? {}) as Partial<IReferralSummary['totalByStatus']>;
  const countCreated = Number(totalByStatus.created ?? 0);
  const countConfirmed = Number(totalByStatus.confirmed ?? 0);
  const countPaid = Number(totalByStatus.paid ?? 0);
  const countExpired = Number(totalByStatus.expired ?? 0);
  const totalCount = summary ? countCreated + countConfirmed + countPaid + countExpired : (referralsData?.count ?? 0);

  const { control, handleSubmit, reset } = useForm<ReferralFormData>({
    resolver: zodResolver(referralFormSchema),
    defaultValues: { document: '', name: '', telephone: '', email: '' },
  });

  const checkMutation = useCheckReferral({
    onError() {
      toast({
        status: 'error',
        title: 'Indicação inválida',
        description: 'Este CPF/CNPJ não pode ser indicado. Verifique se já é membro ou já foi indicado.',
      });
    },
  });

  const createMutation = useCreateReferral({
    onSuccess() {
      reset();
      queryClient.invalidateQueries('referrals');
      queryClient.invalidateQueries('referral-summary');
      onOpen();
    },
    onError() {
      toast({
        status: 'error',
        title: 'Erro ao indicar',
        description: 'Não foi possível registrar a indicação. Tente novamente.',
      });
    },
  });

  const onSubmit = async (data: ReferralFormData) => {
    if (!agreementId) return;

    const cleanDoc = cleanCPFCNPJ(data.document);

    try {
      await checkMutation.mutateAsync({ agreement: agreementId, document: cleanDoc });
    } catch {
      return;
    }

    setSubmittedData(data);

    createMutation.mutate({
      agreement: agreementId,
      document: cleanDoc,
      name: data.name || undefined,
      telephone: data.telephone || undefined,
      email: data.email || undefined,
      referral_value: Number(referralValue ?? 0),
      created_from: 'app',
    });
  };

  return (
    <VStack width="100%" spacing={6} align="stretch">
      {/* Cards side by side */}
      <Grid width="100%" templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }} gap={6}>
        {/* Promo card */}
        <Box borderRadius="xl" overflow="hidden" boxShadow="2px 2px 10px 0px #0000002E" backgroundColor="white">
          {referralImage && (
            <Image src={referralImage} alt="Indique e Ganhe" width="100%" maxHeight="220px" objectFit="cover" />
          )}
          <VStack align="flex-start" spacing={2} padding="20px">
            <Text
              fontSize="18px"
              fontWeight={700}
              color="#292927"
              dangerouslySetInnerHTML={{ __html: referralOffer || 'Indique e Ganhe' }}
            />
            <HStack>
              <Text fontSize="14px" color="#747474">
                Comissão por indicação confirmada:
              </Text>
              <Text fontSize="16px" fontWeight={700} color={colors.primaryColor}>
                R${' '}
                {Number(referralValue ?? 0)
                  .toFixed(2)
                  .replace('.', ',')}
              </Text>
            </HStack>
          </VStack>
        </Box>

        {/* Summary card */}
        <Box borderRadius="lg" padding="16px" backgroundColor={colors.primaryColor} color="white">
          <Heading fontSize="14px" fontWeight={600} mb={3}>
            Resumo das suas indicações
          </Heading>
          <HStack justifyContent="space-between" flexWrap="wrap" spacing={4}>
            <VStack spacing={0}>
              <Text fontSize="20px" fontWeight={700}>
                {totalCount}
              </Text>
              <Text fontSize="11px">Total</Text>
            </VStack>
            <VStack spacing={0}>
              <Text fontSize="20px" fontWeight={700}>
                {countCreated}
              </Text>
              <Text fontSize="11px">Aguardando</Text>
            </VStack>
            <VStack spacing={0}>
              <Text fontSize="20px" fontWeight={700}>
                {countConfirmed}
              </Text>
              <Text fontSize="11px">Confirmadas</Text>
            </VStack>
            <VStack spacing={0}>
              <Text fontSize="20px" fontWeight={700}>
                {countPaid}
              </Text>
              <Text fontSize="11px">Pagas</Text>
            </VStack>
            <VStack spacing={0}>
              <Text fontSize="20px" fontWeight={700}>
                R${' '}
                {Number(summary?.totalPaid ?? 0)
                  .toFixed(2)
                  .replace('.', ',')}
              </Text>
              <Text fontSize="11px">Recebido</Text>
            </VStack>
            <VStack spacing={0}>
              <Text fontSize="20px" fontWeight={700}>
                R${' '}
                {Number(summary?.totalToReceive ?? 0)
                  .toFixed(2)
                  .replace('.', ',')}
              </Text>
              <Text fontSize="11px">A receber</Text>
            </VStack>
          </HStack>
        </Box>
      </Grid>

      {/* Form */}
      <Box borderRadius="lg" padding="20px" boxShadow="2px 2px 6px 0px #0000002E" backgroundColor="white">
        <Heading fontSize="16px" fontWeight={600} color="#292927" mb={4}>
          Fazer uma indicação
        </Heading>
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack spacing={4}>
            <Input control={control} name="document" mask="cpf" label="CPF ou CNPJ do indicado *" />
            <Input control={control} name="name" label="Nome do indicado *" />
            <Input control={control} name="telephone" mask="cell" label="Telefone *" />
            <Input control={control} name="email" label="E-mail *" />
            <Button
              type="submit"
              backgroundColor={colors.primaryColor}
              isLoading={checkMutation.isLoading || createMutation.isLoading}
            >
              Indicar
            </Button>
          </VStack>
        </form>
      </Box>

      {/* Referrals list */}
      <Box>
        <Heading fontSize="16px" fontWeight={600} color="#292927" mb={3}>
          Minhas indicações
        </Heading>
        {loadingReferrals ? (
          <VStack py={6}>
            <Spinner color={colors.primaryColor} />
          </VStack>
        ) : referralsData?.results && referralsData.results.length > 0 ? (
          <VStack spacing={3} align="stretch">
            {referralsData.results.map((referral) => (
              <ReferralListItem key={referral.id} referral={referral} />
            ))}
          </VStack>
        ) : (
          <Box
            padding="20px"
            borderRadius="lg"
            backgroundColor="white"
            boxShadow="2px 2px 6px 0px #0000002E"
            textAlign="center"
          >
            <Text color="#747474" fontSize="14px">
              Você ainda não fez nenhuma indicação.
            </Text>
          </Box>
        )}
      </Box>

      <Divider />

      <ReferralSuccessModal
        isOpen={isOpen}
        onClose={onClose}
        primaryColor={colors.primaryColor}
        referralValue={Number(referralValue ?? 0)}
        submittedData={submittedData}
      />
    </VStack>
  );
}
